import React from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';
import { t } from '@store/I18nStore'

export const ImmunitySection: React.FC = observer(() => {
  const { auth, i18n } = useStore();
  const isLoggedIn = auth.isAuthenticated;
  const t = (key: string) => i18n.t(key);
  return (
  <section id="immunity" className="relative py-14 sm:py-20">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
          <span className="w-8 h-px bg-brand2" /> {t('landing.features.immunity_privacy')} <span className="w-8 h-px bg-brand2" />
        </div>
        <h2 className="reveal text-3xl sm:text-5xl font-extrabold leading-tight">{t('landing.two_sides.title')} <span className="text-gradient">{t('billing.plans.you_see_all')}</span></h2>
        <p className="reveal mt-4 text-slate-400 text-lg">{t('landing.demo.caption')}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 items-stretch">
        <div className="reveal-l lg:col-span-3">
          <div className="relative rounded-2xl border border-white/10 bg-[#0c1027] overflow-hidden shadow-2xl shadow-black/50 flex flex-col h-[520px]">
            <div className="absolute top-2.5 left-4 z-20 text-[10px] uppercase tracking-widest text-brand2/90 font-semibold">{t('nav.console')}</div>
            <div className="flex items-center gap-3 px-4 pt-7 pb-3 border-b border-white/5 bg-[#0a0d22]">
              <button type="button" className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center text-slate-300 shrink-0"><Icon id="ic-arrow-left" className="ic ic-sm" /></button>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-base leading-tight">Chat #665</div>
                <div className="text-[11px] text-slate-500">{t('channels.types.website_widget')} · supadmin.work</div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 grid place-items-center text-emerald-400"><Icon id="ic-arrow-right" className="ic ic-sm" /></span>
                <span className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 grid place-items-center text-rose-400"><Icon id="ic-ban" className="ic ic-sm" /></span>
                <span className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 grid place-items-center text-amber-400"><Icon id="ic-logout" className="ic ic-sm" /></span>
              </div>
            </div>
            <div id="opBody" className="chatscroll p-4 space-y-3 overflow-y-auto flex-1 min-h-0" />
            <div className="relative">
              <div id="opQuick" className="absolute left-3 right-3 bottom-full mb-2 rounded-xl border border-white/10 bg-[#11152a] shadow-2xl shadow-black/60 overflow-hidden opacity-0 translate-y-2 pointer-events-none transition-all duration-300">
                <div className="px-3 py-2.5 border-b border-white/5">
                  <div className="h-9 rounded-lg bg-white/5 border border-white/10 px-3 flex items-center text-xs text-slate-400">
                    <Icon id="ic-zap" className="ic ic-sm mr-2 text-slate-500" />
                    <span id="opSearch">{t('templates.search.shortcut_phrase_section')}</span>
                  </div>
                </div>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{t('help.search.best_matches')}</div>
                <div id="opSuggest" className="px-2 pb-2 space-y-1" />
              </div>
              <div className="flex items-center gap-2 px-3 py-3 border-t border-white/5 bg-[#0a0d22]">
                <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-slate-400 shrink-0"><Icon id="ic-paperclip" className="ic ic-sm" /></span>
                <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-slate-400 shrink-0"><Icon id="ic-smile" className="ic ic-sm" /></span>
                <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 grid place-items-center text-brand2 font-bold shrink-0">/</span>
                <div className="flex-1 h-9 rounded-lg bg-white/5 border border-white/10 px-3 flex items-center text-sm overflow-hidden">
                  <span id="opInput" className="text-slate-100 whitespace-nowrap" />
                  <span id="opPlaceholder" className="text-slate-500">{t('chat.composer.placeholder')}</span>
                </div>
                <button type="button" className="grid place-items-center w-9 h-9 rounded-lg bg-gradient-to-br from-brand to-indigo-500 text-white shrink-0"><Icon id="ic-send" className="ic ic-sm" /></button>
              </div>
            </div>
          </div>
        </div>

        <div className="reveal-r lg:col-span-2">
          <div className="relative">
            <div className="absolute -inset-4 bg-brand/20 blur-3xl rounded-[2.5rem] -z-10" />
            <div className="relative rounded-[1.8rem] overflow-hidden border border-white/10 shadow-2xl shadow-black/60 bg-[#16181f] flex flex-col h-[520px]">
              <div className="absolute top-2.5 right-5 z-20 text-[10px] uppercase tracking-widest text-brand2/90 font-semibold">{t('widget.preview.client_widget')}</div>
              <div className="flex items-center gap-3 px-4 pt-7 pb-3 bg-[#0e0f13]">
                <button type="button" className="text-white/80"><Icon id="ic-arrow-left" className="ic ic-sm" /></button>
                <div className="relative grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-brand to-indigo-500 font-bold text-white text-sm">
                  X
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0e0f13]" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm leading-tight">{t('team.roles.manager')} ChatX</div>
                  <div className="text-[11px] text-emerald-400">● {t('app.status.online').toLowerCase()}</div>
                </div>
                <div className="ml-auto flex items-center gap-3 text-white/70">
                  <Icon id="ic-minus" className="ic ic-sm" />
                  <Icon id="ic-x" className="ic ic-sm" />
                </div>
              </div>
              <div className="relative flex-1 min-h-0">
                <div id="chatBody2" className="chatscroll px-4 py-4 space-y-1.5 h-full overflow-y-auto bg-[#16181f]" />
                <div id="chatGreeting" className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6 text-center bg-[#16181f]">
                  <div className="grid place-items-center w-20 h-20 rounded-2xl bg-white shadow-xl"><span className="wave-hand text-4xl">👋</span></div>
                  <div className="text-lg font-bold">{t('demo.messages.hello')}</div>
                  <div className="text-sm text-slate-400">{t('widget.client.ready_your_questions')}</div>
                  <button type="button" id="chatOpenBtn" className="mt-2 px-5 py-2.5 rounded-xl bg-brand hover:bg-blue-600 font-semibold text-sm shadow-lg shadow-brand/30 transition active:scale-95">{t('widget.client.online_ask')} →</button>
                </div>
              </div>
              <div className="px-3 py-3 bg-[#0e0f13]">
                <div className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 pl-4 pr-1.5 h-12">
                  <span className="flex-1 text-sm text-slate-500 truncate">{t('widget.client.message_placeholder')}…</span>
                  <button type="button" className="grid place-items-center w-9 h-9 rounded-full text-white shrink-0" style={{ background: '#37597a' }}><Icon id="ic-arrow-up" className="ic ic-sm" /></button>
                </div>
                <div className="flex items-center gap-4 mt-2 px-1.5 text-slate-500">
                  <Icon id="ic-plus" className="ic ic-sm" />
                  <Icon id="ic-smile" className="ic ic-sm" />
                  <span className="mx-auto text-[11px] text-slate-600">Powered by <span className="text-brand2 font-semibold">ChatX</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="reveal mt-9 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-slate-300 text-sm">
        <span className="inline-flex items-center gap-2"><Icon id="ic-check" className="ic ic-sm text-emerald-400" /> {t('billing.features.e_2e_encryption')}</span>
        <span className="inline-flex items-center gap-2"><Icon id="ic-check" className="ic ic-sm text-emerald-400" /> {t('landing.features.zero_logs')}</span>
        <span className="inline-flex items-center gap-2"><Icon id="ic-check" className="ic ic-sm text-emerald-400" /> {t('landing.features.offshore')}</span>
      </div>
      <div className="reveal text-center mt-7">
        {isLoggedIn ? (
          <Link to="/myProfile" className="inline-flex px-7 py-3.5 rounded-2xl bg-brand hover:bg-blue-600 font-semibold shadow-xl shadow-brand/30 transition active:scale-95">{t('nav.to_profile')}</Link>
        ) : (
          <a href="#cta" className="inline-flex px-7 py-3.5 rounded-2xl bg-brand hover:bg-blue-600 font-semibold shadow-xl shadow-brand/30 transition active:scale-95">{t('landing.cta.connect_free')}</a>
        )}
      </div>
    </div>
  </section>
  );
});
